import { cn } from '@virginexperiencedays/tailwind';
import { LinkWrap } from '@virginexperiencedays/components-v2/src/navigation/LinkWrap';

import { ReviewCard, ReviewHeading, ReviewHeadingSkeleton, ReviewCardSkeleton } from './Reviews';

import type { ReviewsOverlayProps } from './ReviewsOverlay';

type ReviewsOverlayContentProps = Pick<
  ReviewsOverlayProps,
  'averageRating' | 'count' | 'reviews' | 'onExpandCollapse'
>;

export const ReviewsOverlayContent = ({
  averageRating,
  count,
  reviews,
  onExpandCollapse,
}: ReviewsOverlayContentProps) => (
  <div className="flex flex-col gap-4">
    <ReviewHeading averageRating={averageRating} count={count} size="small" />

    <div
      className={cn(
        'flex flex-col gap-8',
        'overflow-y-auto !pr-5 mega-nav-scrollbar',
        // drawer
        // 90dvh less heights of drawer handle, section heading, rating summary,
        // the gap between rating summary and this, and drawer body padding bottom respectively
        'max-h-[calc(90dvh_-_(20px_+_54px_+_22px_+_16px_+_16px))] after:pb-4',
        // modal
        'md:max-h-[calc(600px_-_146px)] md:after:hidden md:after:pb-0'
      )}
    >
      {reviews.map(({ reply, responderName, ...review }, index) => (
        <ReviewCard
          key={review.title}
          {...review}
          response={{ content: reply, responderName }}
          onExpandCollapse={({ isExpanded }) => {
            onExpandCollapse?.({
              currentIndex: index,
              total: reviews?.length,
              isExpanded,
              rating: review?.rating,
              dateReviewed: review?.dateCreated,
              moduleTitle: review?.title,
            });
          }}
        />
      ))}

      <div className="text-center">
        <LinkWrap
          href={`${process.env.NEXT_PUBLIC_HOME_LINK}/reviews`}
          className="text-sm font-semibold leading-normal text-link underline underline-offset-4"
          isExternal
        >
          Read reviews about all our experiences
        </LinkWrap>
      </div>
    </div>
  </div>
);

export const ReviewsOverlaySkeleton = ({ length = 3 }: { length?: number }) => (
  <div className="flex flex-col gap-4">
    <ReviewHeadingSkeleton />

    <div
      className={cn(
        'flex flex-col gap-8',
        'overflow-y-auto !pr-5 mega-nav-scrollbar',
        // drawer
        // 90dvh less heights of drawer handle, section heading, rating summary,
        // the gap between rating summary and this, and drawer body padding bottom respectively
        'max-h-[calc(90dvh_-_(20px_+_54px_+_22px_+_16px_+_16px))] after:pb-4',
        // modal
        'md:max-h-[calc(600px_-_146px)] md:after:pb-0'
      )}
    >
      {Array.from({ length }, (_, i) => (
        <ReviewCardSkeleton key={i} />
      ))}
    </div>
  </div>
);

export default ReviewsOverlayContent;
